import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, Sparkles, Star, Zap, Clock, Users, Vote, Lock } from 'lucide-react';

const PricingSection = ({ onCTAClick, spotsLeft }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const founderFeatures = [
    'Hasta 50 pacientes activos',
    'Mensajes ilimitados',
    'Clon de personalidad IA',
    'Validación de fotos de comida',
    'Sustituciones inteligentes',
    'Dashboard de métricas',
    'Soporte prioritario',
  ];

  const founderPerks = [
    { icon: Lock, text: 'Precio de fundador de por vida' },
    { icon: Users, text: 'Onboarding 1:1 personalizado' },
    { icon: Vote, text: 'Voto en roadmap de producto' },
    { icon: Star, text: 'Acceso a grupo privado de fundadores' },
  ];

  return (
    <section id="precios" ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF9]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#D95A39] mb-4">
            Oferta exclusiva
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight mb-4">
            Acceso Fundadores
            <span className="text-[#2E5C41]"> — Solo {spotsLeft} cupos</span>
          </h2>
          <p className="text-base md:text-lg text-[#4B5E53] leading-relaxed">
            Sé de los primeros en transformar tu práctica. Precio especial + beneficios exclusivos.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          {/* Regular Plan - Faded */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative p-8 bg-white/50 rounded-3xl border border-[#2E5C41]/10"
          >
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-3xl flex items-center justify-center">
              <div className="text-center">
                <span className="text-sm text-[#4B5E53]">Disponible después del lanzamiento</span>
              </div>
            </div>
            
            <div className="opacity-30">
              <span className="text-xs font-bold uppercase tracking-wider text-[#4B5E53]">Plan Regular</span>
              <div className="mt-4 mb-6">
                <span className="font-cabinet text-4xl font-bold text-[#1A2E24]">$49</span>
                <span className="text-[#4B5E53]">/mes</span>
              </div>
              <ul className="space-y-3">
                {founderFeatures.slice(0, 5).map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm text-[#4B5E53]">
                    <Check className="w-4 h-4 text-[#849B89]" strokeWidth={1.5} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Founder Plan - Highlighted */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#2E5C41] to-[#849B89] rounded-3xl blur opacity-20" />
            
            <div className="relative p-8 bg-white rounded-3xl border-2 border-[#2E5C41] shadow-xl">
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#D95A39] text-white text-xs font-bold rounded-full">
                  <Sparkles className="w-3 h-3" />
                  ACCESO FUNDADORES
                </span>
              </div>

              <div className="mt-4">
                <span className="text-xs font-bold uppercase tracking-wider text-[#2E5C41]">Oferta Limitada</span>
                <div className="mt-4 mb-2">
                  <span className="text-[#4B5E53] line-through text-lg">$49</span>
                </div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-cabinet text-5xl font-bold text-[#1A2E24]">$29</span>
                  <span className="text-[#4B5E53]">/mes</span>
                  <span className="px-2 py-0.5 bg-[#D95A39]/10 text-[#D95A39] text-xs font-bold rounded">
                    -40%
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {founderFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-sm text-[#1A2E24]">
                      <div className="w-5 h-5 rounded-full bg-[#2E5C41]/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#2E5C41]" strokeWidth={2} />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Founder Perks */}
                <div className="p-4 bg-[#F2F4F0] rounded-2xl mb-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#2E5C41] mb-3">
                    + Beneficios exclusivos de fundador
                  </p>
                  <ul className="space-y-2">
                    {founderPerks.map((perk, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-[#4B5E53]">
                        <perk.icon className="w-4 h-4 text-[#2E5C41]" strokeWidth={1.5} />
                        {perk.text}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <motion.button
                  onClick={onCTAClick}
                  className="w-full py-4 bg-[#2E5C41] text-white font-bold rounded-full hover:bg-[#1A2E24] transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="cta-pricing"
                >
                  <Zap className="w-5 h-5" strokeWidth={1.5} />
                  Reservar mi cupo ahora
                </motion.button>

                {/* Trust */}
                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[#4B5E53]">
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-[#2E5C41]" />
                    30 días de garantía
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-[#2E5C41]" />
                    Cancela cuando quieras
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Urgency reminder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#D95A39]/10 rounded-full">
            <div className="w-2 h-2 bg-[#D95A39] rounded-full animate-pulse" />
            <span className="text-sm font-medium text-[#1A2E24]">
              Solo quedan <span className="font-bold text-[#D95A39]">{spotsLeft} de 15</span> cupos de fundador
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
